import { getActivePage, type CurvaDocument } from "@/document/model/document";
import {
  collectResolvedGuides,
  createGuide,
  guideLocalPosition,
  guideScopesEqual,
  type Guide,
  type GuideOrientation,
  type GuideScope,
  type GuideSelection,
} from "@/document/model/guides";
import type { FrameNode } from "@/document/model/nodes";
import type { ViewportScreenInsets } from "@/renderer/renderHelpers";
import type { DocumentGuideOverlay } from "@/renderer/overlayTypes";
import { findDeepestFrameAtPoint } from "./canvasGeometry";
import { RULER_SIZE } from "./RulerOverlay";

export function pointInsideCanvasContentArea(
  clientX: number,
  clientY: number,
  canvas: HTMLCanvasElement,
  insets: ViewportScreenInsets,
): boolean {
  const rect = canvas.getBoundingClientRect();
  return clientX >= rect.left + insets.left + RULER_SIZE
    && clientY >= rect.top + insets.top + RULER_SIZE
    && clientX <= rect.right - insets.right
    && clientY <= rect.bottom - insets.bottom;
}

export function guideDocumentPositionFromPointer(
  orientation: GuideOrientation,
  point: { x: number; y: number },
): number {
  return orientation === "vertical" ? point.x : point.y;
}

export function resolveGuidePlacement(
  document: CurvaDocument,
  activePageId: string | undefined,
  orientation: GuideOrientation,
  point: { x: number; y: number },
): { scope: GuideScope; guide: Guide } | undefined {
  const page = getActivePage(document, activePageId);
  if (!page) {
    return undefined;
  }

  const documentPosition = guideDocumentPositionFromPointer(orientation, point);
  const scope = resolveGuideScopeAtPoint(document, activePageId, point);
  if (!scope) {
    return undefined;
  }

  return {
    scope,
    guide: createGuide(orientation, guidePositionForScope(document, scope, orientation, documentPosition)),
  };
}

export function resolveGuideScopeAtPoint(
  document: CurvaDocument,
  activePageId: string | undefined,
  point: { x: number; y: number },
): GuideScope | undefined {
  const page = getActivePage(document, activePageId);
  if (!page) {
    return undefined;
  }

  const frame = findDeepestFrameAtPoint(document, point, activePageId);
  return frame ? { kind: "frame", frameId: frame.id } : { kind: "page", pageId: page.id };
}

export function guidePositionForScope(
  document: CurvaDocument,
  scope: GuideScope,
  orientation: GuideOrientation,
  documentPosition: number,
): number {
  if (scope.kind === "page") {
    return documentPosition;
  }

  const frame = findFrameById(getActivePage(document)?.children ?? [], scope.frameId);
  if (!frame) {
    return documentPosition;
  }

  return guideLocalPosition(createGuide(orientation, 0), frame, documentPosition);
}

export function localGuidePosition(
  document: CurvaDocument,
  selection: GuideSelection,
  orientation: GuideOrientation,
  documentPosition: number,
): number {
  return guidePositionForScope(document, selection.scope, orientation, documentPosition);
}

export function buildDocumentGuideOverlay(
  document: CurvaDocument,
  activePageId: string | undefined,
  selectedNodeIds: string[],
  selectedGuide: GuideSelection | undefined,
): DocumentGuideOverlay[] {
  return collectResolvedGuides(document, activePageId, selectedNodeIds).map(guide => ({
    id: guide.id,
    orientation: guide.orientation,
    documentPosition: guide.documentPosition,
    clipBounds: guide.clipBounds,
    selected: selectedGuide
      ? guide.id === selectedGuide.id && guideScopesEqual(guide.scope, selectedGuide.scope)
      : false,
  }));
}

export function guideSelectionFromResolved(guide: { id: string; scope: GuideScope }): GuideSelection {
  return { id: guide.id, scope: guide.scope };
}

function findFrameById(nodes: FrameNode["children"], frameId: string): FrameNode | undefined {
  for (const node of nodes) {
    if (node.type !== "frame") {
      continue;
    }

    if (node.id === frameId) {
      return node;
    }

    const nested = findFrameById(node.children, frameId);
    if (nested) {
      return nested;
    }
  }

  return undefined;
}
